import { Tag } from "antd";
import { CalendarOutlined, ClockCircleOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { EducationType } from "../../types/client-education-detail.types";
import type { EducationDetailDto } from "../../types/client-education-detail.types";
import styles from "./Hero.module.scss";

const TYPE_LABEL: Record<EducationType, { labelKey: string; color: string }> = {
  [EducationType.ON_SITE]: { labelKey: "requestAndApproval.educationTypes.faceToFace", color: "orange" },
  [EducationType.ONLINE]: { labelKey: "requestAndApproval.educationTypes.online", color: "green" },
  [EducationType.UNIT]: { labelKey: "requestAndApproval.educationTypes.unit", color: "blue" },
  [EducationType.NONUNIT]: { labelKey: "requestAndApproval.educationTypes.nonUnit", color: "cyan" },
};

function formatDuration(
  h: number | undefined,
  m: number | undefined,
  hLabel: string,
  mLabel: string,
): string | null {
  const hours = h ?? 0;
  const mins = m ?? 0;
  if (!hours && !mins) return null;
  if (!hours) return `${mins} ${mLabel}`;
  if (!mins) return `${hours} ${hLabel}`;
  return `${hours} ${hLabel} ${mins} ${mLabel}`;
}

function formatDateRange(start?: string | null, end?: string | null): string | null {
  if (!start && !end) return null;
  const fmt = (d?: string | null) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  return fmt(start ?? end);
}

export function HeroSection(props: { education: EducationDetailDto }) {
  const { t } = useTranslation();
  const e = props.education;
  const typeCfg = TYPE_LABEL[e.type] ?? { labelKey: "", color: "default" };
  const typeLabel = typeCfg.labelKey ? t(typeCfg.labelKey) : "-";
  const duration = formatDuration(
    e.durationHours,
    e.durationMinutes,
    t("clientHero.hoursShort"),
    t("clientHero.minutesShort"),
  );
  const dateRange = formatDateRange(e.startDate, e.endDate);
  const instructorNames = (e.instructors ?? [])
    .map((i) => i.name ?? `${i.firstName ?? ""} ${i.lastName ?? ""}`.trim())
    .filter(Boolean);

  return (
    <section id="hero" className={styles.hero}>
      {e.coverImageUrl && (
        <div
          className={styles.cover}
          style={{ backgroundImage: `url(${e.coverImageUrl})` }}
          role="img"
          aria-label={e.name}
        />
      )}

      <div className={styles.body}>
        <div className={styles.chips}>
          <Tag color={typeCfg.color}>{typeLabel}</Tag>
          {e.isRequired && <Tag color="red">{t("clientHero.mandatory")}</Tag>}
          {e.categories?.slice(0, 3).map((c) => (
            <Tag key={c.id}>{c.name}</Tag>
          ))}
        </div>

        <h1 className={styles.title}>{e.name}</h1>
        {e.code && <span className={styles.code}>{e.code}</span>}

        <div className={styles.metaRow}>
          {dateRange && (
            <span className={styles.metaItem}>
              <CalendarOutlined />
              {dateRange}
            </span>
          )}
          {duration && (
            <span className={styles.metaItem}>
              <ClockCircleOutlined />
              {duration}
            </span>
          )}
          {e.capacity != null && (
            <span className={styles.metaItem}>
              <TeamOutlined />
              {t("clientHero.capacity", { count: e.capacity })}
            </span>
          )}
          {instructorNames.length > 0 && (
            <span className={styles.metaItem}>
              <UserOutlined />
              {instructorNames.join(", ")}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
